import {
  Badge,
  Button,
  cn,
  Command,
  CommandContent,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandSheet,
  CommandSheetContent,
  CommandSheetItem,
  CommandSheetList,
  CommandSheetTrigger,
  InputGroup,
  InputLeftContent,
  mediaQueries,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@mozioinc/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "usehooks-ts";
import TranslateIcon from "../assets/glob.svg?react";
import { useUserData } from "../hooks/use-user-data";
import {
  languageToOldFormat,
  languages as supportedLanguages,
  replaceLocaleInPath,
} from "../utils/languages";
import { captureLanguageChanged } from "../utils/posthog";
import type { TransNamespace } from "../types";

const supportedLocales = new Set(supportedLanguages.map((l) => l.locale));

type LanguagePickerProps = {
  compactMapOverlay?: boolean;
};

export const LanguagePicker = ({
  compactMapOverlay = false,
}: LanguagePickerProps) => {
  const { t, i18n } = useTranslation<TransNamespace>("transfer:tracking");
  const { user } = useUserData();
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery(mediaQueries.lg);

  const languages = supportedLanguages.map((l) => ({
    ...l,
    label: i18n.language === "en_US" ? `${l.name} (${l.englishName})` : l.name,
    value:
      i18n.language === "en_US"
        ? `${l.name} (${l.englishName})`.toLowerCase()
        : l.name.toLowerCase(),
  }));

  const currentLanguage =
    languages.find((l) => l.locale === languageToOldFormat(i18n.language)) ??
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    languages[0]!;

  const handleLocaleChange = (value: string) => {
    const language = languages.find((l) => l.value === value);
    if (!language) return;

    if (user) {
      captureLanguageChanged({
        userDataLocale: user.locale,
        newLocale: language.locale,
      });
    }

    const url = new URL(window.location.href);
    const newPathname = replaceLocaleInPath(
      url.pathname,
      language.locale,
      supportedLocales,
    );
    if (newPathname !== undefined) {
      url.pathname = newPathname;
      window.location.href = url.toString();
    }
  };

  const trigger = (
    <button
      type="button"
      className={cn(
        compactMapOverlay
          ? "flex shrink-0 items-center gap-2 rounded-xl bg-white px-2.5 py-2 text-neutral-900 shadow-[0_2px_10px_rgba(0,0,0,0.12)] active:bg-neutral-50"
          : "flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-neutral-900 hover:bg-neutral-100",
      )}
      aria-label={compactMapOverlay ? t("Language") : undefined}
    >
      <TranslateIcon
        className={cn("shrink-0", compactMapOverlay ? "size-[18px]" : "size-4")}
      />
      {compactMapOverlay ? null : <span>{currentLanguage.name}</span>}
      <svg
        className={cn(
          "size-3 shrink-0 transition-transform duration-200",
          open && "rotate-180",
        )}
        viewBox="0 0 10 6"
        fill="none"
      >
        <path
          d="M1 1l4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  if (!isDesktop) {
    return (
      <CommandSheet
        label={t("Language")}
        open={open}
        value={currentLanguage.value}
        onOpenChange={setOpen}
        onValueChange={handleLocaleChange}
      >
        <CommandSheetTrigger asChild>{trigger}</CommandSheetTrigger>
        <CommandSheetContent shouldFilter>
          <InputGroup>
            <InputLeftContent>
              <svg
                viewBox="0 0 24 24"
                className="size-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </InputLeftContent>
            <CommandInput />
          </InputGroup>
          <CommandSheetList>
            <CommandEmpty>{t("No results found")}</CommandEmpty>
            {languages.map((lang) => (
              <CommandSheetItem key={lang.value} value={lang.value}>
                {lang.label}
              </CommandSheetItem>
            ))}
          </CommandSheetList>
        </CommandSheetContent>
      </CommandSheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent sideOffset={8} align="end">
        <Command shouldFilter={false}>
          <InputGroup>
            <InputLeftContent>
              <svg
                viewBox="0 0 24 24"
                className="size-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </InputLeftContent>
            <CommandInput className="h-10" />
          </InputGroup>
          <CommandContent className="-mr-2 p-0">
            <CommandEmpty>{t("No results found")}</CommandEmpty>
            {languages.map((lang) => (
              <CommandItem
                key={lang.value}
                className={cn("aria-selected:bg-neutral-100", {
                  "font-semibold text-neutral-900":
                    lang.value === currentLanguage.value,
                })}
                onSelect={() => {
                  setOpen(false);
                  handleLocaleChange(lang.value);
                }}
              >
                {lang.label}
              </CommandItem>
            ))}
          </CommandContent>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
